/**
 * Tabs Component
 * Tabbed navigation component
 */

import { useState } from 'react'
import { cn } from '@/lib/utils'

interface Tab {
  id: string
  label: string
  icon?: React.ReactNode
  disabled?: boolean
}

interface TabsProps {
  tabs: Tab[]
  defaultTab?: string
  onChange?: (tabId: string) => void
  variant?: 'default' | 'pills' | 'underline'
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  className?: string
}

export function Tabs({
  tabs,
  defaultTab,
  onChange,
  variant = 'default',
  size = 'md',
  fullWidth = false,
  className,
}: TabsProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id)

  const handleTabChange = (tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId)
    if (tab && !tab.disabled) {
      setActiveTab(tabId)
      onChange?.(tabId)
    }
  }

  const variantStyles = {
    default: {
      container: 'bg-muted p-1 rounded-lg inline-flex gap-1',
      tab: 'px-4 py-2 rounded-md transition-all duration-200',
      active: 'bg-card text-foreground shadow-sm',
      inactive: 'text-muted-foreground hover:text-foreground hover:bg-card/50',
    },
    pills: {
      container: 'inline-flex gap-2',
      tab: 'px-4 py-2 rounded-full transition-all duration-200 border border-transparent',
      active: 'bg-primary text-primary-foreground border-primary',
      inactive: 'text-muted-foreground hover:text-foreground hover:border-border',
    },
    underline: {
      container: 'border-b border-border flex gap-4',
      tab: 'px-1 py-3 transition-all duration-200 relative',
      active: 'text-foreground',
      inactive: 'text-muted-foreground hover:text-foreground',
      activeIndicator: 'absolute bottom-0 left-0 right-0 h-0.5 bg-primary',
    },
  }

  const sizeStyles = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  }

  const styles = variantStyles[variant]

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      <div className={cn(styles.container, fullWidth && 'w-full justify-start')}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              disabled={tab.disabled}
              className={cn(
                styles.tab,
                sizeStyles[size],
                isActive ? styles.active : styles.inactive,
                tab.disabled && 'opacity-50 cursor-not-allowed',
                fullWidth && 'flex-1',
                'flex items-center gap-2 font-medium'
              )}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {variant === 'underline' && isActive && (
                <div className={styles.activeIndicator || ''} />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Tabs Panel Container
interface TabsPanelProps {
  children: React.ReactNode
  className?: string
}

export function TabsPanel({ children, className }: TabsPanelProps) {
  return <div className={cn('mt-6', className)}>{children}</div>
}

// Tab Panel (for conditional rendering)
interface TabPanelProps {
  value: string
  activeTab: string
  children: React.ReactNode
}

export function TabPanel({ value, activeTab, children }: TabPanelProps) {
  if (value !== activeTab) return null
  return <div>{children}</div>
}
